/**
 * src/domain/inventory/ProductFactory.ts
 *
 * Pure Invariant Validator & Factory for Typed Domain Products.
 */

import { ArtworkProduct, PosProduct, KioskProduct, ArtworkMetadata, PosProductMetadata, KioskProductMetadata, PublicPrice, ArtworkImageRecord } from './types/product-types';

export class ProductFactory {
  public static normalizeArtworkImages(images: Array<Partial<ArtworkImageRecord> & { url: string }>): ArtworkImageRecord[] {
    if (!images || images.length === 0) return [];
    return images.map((img) => ({
      url: img.url || '',
      caption: img.caption,
      isPrimary: typeof img.isPrimary === 'boolean' ? img.isPrimary : true,
      role: img.role || 'primary',
      fitMode: img.fitMode || 'contain',
    }));
  }

  public static normalizeArtwork(artwork: ArtworkProduct): ArtworkProduct {
    return {
      ...artwork,
      title: artwork.title.trim(),
      description: artwork.description?.trim() || undefined,
      metadata: {
        ...artwork.metadata,
        images: ProductFactory.normalizeArtworkImages(artwork.metadata.images),
      },
    };
  }

  public static createArtwork(payload: {
    tenantId: string;
    sku: string;
    title: string;
    description?: string;
    price: { amount: number | null; currency: string; isPrivate?: boolean };
    metadata: ArtworkMetadata | (Omit<ArtworkMetadata, 'images'> & { images: Array<Partial<ArtworkImageRecord> & { url: string }> });
  }): ArtworkProduct {
    // 1. SKU validation
    if (!/^[A-Z0-9_-]{3,32}$/i.test(payload.sku)) {
      throw new Error(`[ProductFactory] Invalid SKU format: ${payload.sku}`);
    }

    // 2. Title bounds
    if (!payload.title || payload.title.trim().length === 0 || payload.title.length > 200) {
      throw new Error('[ProductFactory] Title must be between 1 and 200 characters');
    }

    // 3. Price validation & discriminated PublicPrice construction
    const isPrivate = !!payload.price.isPrivate;
    let price: PublicPrice;
    if (isPrivate) {
      price = { isPrivate: true, amount: null, currency: payload.price.currency || 'USD', formatted: 'Price on Request' };
    } else {
      if (typeof payload.price.amount !== 'number' || payload.price.amount <= 0) {
        throw new Error('[ProductFactory] Public price amount must be greater than 0');
      }
      price = {
        isPrivate: false,
        amount: payload.price.amount,
        currency: payload.price.currency || 'USD',
        formatted: `${payload.price.currency || 'USD'} ${payload.price.amount.toLocaleString()}`,
      };
    }

    // 4. Creation year sanity
    const currentYear = new Date().getFullYear();
    if (payload.metadata.yearCreated < 1000 || payload.metadata.yearCreated > currentYear) {
      throw new Error(`[ProductFactory] Invalid yearCreated: ${payload.metadata.yearCreated}`);
    }

    // 5. Dimension sanity
    if (payload.metadata.dimensions.width <= 0 || payload.metadata.dimensions.height <= 0) {
      throw new Error('[ProductFactory] Artwork dimensions must be strictly positive');
    }

    // 6. Edition sanity
    if (payload.metadata.edition && payload.metadata.edition.current > payload.metadata.edition.total) {
      throw new Error('[ProductFactory] Edition current number cannot exceed edition total');
    }

    // 7. Images bound
    if (!payload.metadata.images || payload.metadata.images.length === 0) {
      throw new Error('[ProductFactory] Artwork requires at least one primary image');
    }
    if (payload.metadata.images.length > 20) {
      throw new Error('[ProductFactory] Artwork cannot exceed 20 images');
    }

    const normalizedImages = ProductFactory.normalizeArtworkImages(payload.metadata.images);

    const now = new Date().toISOString();
    return {
      id: `art_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      tenantId: payload.tenantId,
      sku: payload.sku,
      title: payload.title.trim(),
      description: payload.description?.trim() || undefined,
      type: 'artwork',
      status: 'active',
      version: 1,
      price,
      metadata: {
        ...payload.metadata,
        images: normalizedImages,
        provenance: payload.metadata.provenance || [],
      },
      createdAt: now,
      updatedAt: now,
    };
  }

  public static createPosProduct(payload: {
    tenantId: string;
    sku: string;
    title: string;
    description?: string;
    price: { amount: number; currency: string };
    metadata: PosProductMetadata;
  }): PosProduct {
    if (payload.price.amount <= 0) {
      throw new Error('[ProductFactory] POS price amount must be greater than 0');
    }
    if (payload.metadata.stockLevel < 0) {
      throw new Error('[ProductFactory] Stock level cannot be negative');
    }

    const now = new Date().toISOString();
    return {
      id: `pos_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      tenantId: payload.tenantId,
      sku: payload.sku,
      title: payload.title.trim(),
      description: payload.description?.slice(0, 2000),
      type: 'pos',
      status: 'active',
      version: 1,
      price: {
        isPrivate: false,
        amount: payload.price.amount,
        currency: payload.price.currency || 'USD',
        formatted: `${payload.price.currency || 'USD'} ${payload.price.amount.toFixed(2)}`,
      },
      metadata: payload.metadata,
      createdAt: now,
      updatedAt: now,
    };
  }

  public static createKioskProduct(payload: {
    tenantId: string;
    sku: string;
    title: string;
    description?: string;
    price: { amount: number; currency: string };
    metadata: KioskProductMetadata;
  }): KioskProduct {
    const now = new Date().toISOString();
    return {
      id: `kiosk_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      tenantId: payload.tenantId,
      sku: payload.sku,
      title: payload.title.trim(),
      description: payload.description?.slice(0, 2000),
      type: 'kiosk',
      status: 'active',
      version: 1,
      price: {
        isPrivate: false,
        amount: payload.price.amount,
        currency: payload.price.currency || 'USD',
        formatted: `${payload.price.currency || 'USD'} ${payload.price.amount.toFixed(2)}`,
      },
      metadata: payload.metadata,
      createdAt: now,
      updatedAt: now,
    };
  }
}
